import React, { memo, useCallback, useState } from "react";
import { ReactSVG } from "react-svg";
import { useTranslate } from "helpers";
import { FileExtended } from "../FileDropZone/types";
import { Dialog } from "../../Dialog";
import { PictureIcon } from "../../Pictures/PictureIcon";

interface FilePreviewGridProps {
  files: FileExtended[];
  onDelete: (file: FileExtended) => void;
  thumbSize?: "small" | "medium" | "large";
  columns?: number;
  label?: string;
}

const FilePreviewGrid: React.FC<FilePreviewGridProps> = ({
  files,
  onDelete,
  thumbSize = "small",
  columns = 5,
  label,
}) => {
  const { t } = useTranslate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileExtended | null>(null);

  const handleDeleteClick = useCallback(
    (file: FileExtended) => (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedFile(file);
      setOpenDialog(true);
    },
    []
  );

  const handleDeleteConfirm = useCallback(() => {
    if (selectedFile) {
      onDelete(selectedFile);
      setOpenDialog(false);
      setSelectedFile(null);
    }
  }, [onDelete, selectedFile]);

  const handleDeleteCancel = useCallback(() => {
    setSelectedFile(null);
    setOpenDialog(false);
  }, []);

  if (files.length === 0) return null;

  const gridClassName = `grid gap-3 grid-cols-${columns}`;

  return (
    <>
      <div className="max-h-72 overflow-y-scroll">
        {label && (
          <div className="text-sm font-light mb-2">{label}</div>
        )}
        <div className={gridClassName}>
          {files.map((file) => (
            <div
              key={file.id}
              className="relative border cursor-pointer bg-slate-50 border-gray-200 rounded flex justify-center items-center mb-2 overflow-hidden h-28 w-full group"
            >
              {file.url && <PictureIcon url={file.url} size={thumbSize} />}
              <span
                className="absolute top-2 right-2 bg-white border-2 border-red-300 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-red-100 z-10"
                onClick={handleDeleteClick(file)}
              >
                <ReactSVG
                  src="/media/icons/close.svg"
                  className="w-4 h-4 block rounded-full stroke-red-700 stroke-2"
                />
              </span>
            </div>
          ))}
        </div>
      </div>
      <Dialog
        open={openDialog}
        title={t("admin.fileupload.dialog.title")}
        message={t("admin.fileupload.dialog.message")}
        confirmText={t("admin.fileupload.dialog.confirmtext")}
        cancelText={t("admin.fileupload.dialog.canceltext")}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

export default memo(FilePreviewGrid);
